import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, forkJoin, map, of, switchMap, take } from 'rxjs';
import { Perfil, PerfilFiltros } from '../../core/models/perfil.model';
import { PerfilService } from '../../core/services/perfil.service';
import { ToastService } from '../../core/services/toast.service';
import { LoaderService } from '../../shared/services/loader/loader.service';
import { formatDateIsoToBr } from '../../core/utils/date-format.util';
import { StatusBadgeComponent } from '../../shared/components/status-badge/status-badge.component';
import { BackButtonComponent } from '../../shared/components/back-button/back-button.component';
import { EmptyStateComponent } from '../../shared/components/empty-state/empty-state.component';

type FiltroStatus = 'todos' | 'Ativo' | 'Inativo';

interface FiltroOption {
  readonly value: FiltroStatus;
  readonly label: string;
}

@Component({
  selector: 'app-perfil-detalhe',
  standalone: true,
  imports: [StatusBadgeComponent, BackButtonComponent, EmptyStateComponent],
  templateUrl: './perfil-detalhe.component.html',
  styleUrl: './perfil-detalhe.component.scss',
})
export class PerfilDetalheComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly perfilService = inject(PerfilService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);

  /** Modo configurações gerais (todos os perfis) vs. visualização individual. */
  isConfiguracoesMode = false;

  readonly formatDate = formatDateIsoToBr;

  // ---- Modo individual ----
  private readonly perfilSignal = signal<Perfil | null>(null);
  readonly perfil = this.perfilSignal.asReadonly();
  readonly naoEncontrado = signal(false);

  // ---- Modo configurações gerais ----
  private readonly perfisSignal = signal<readonly Perfil[]>([]);

  /** Filtro inicial da tela: exibe somente os perfis ativos. */
  readonly filtroStatus = signal<FiltroStatus>('Ativo');

  readonly filtroOptions: readonly FiltroOption[] = [
    { value: 'todos', label: 'Todos' },
    { value: 'Ativo', label: 'Ativos' },
    { value: 'Inativo', label: 'Inativos' },
  ];

  /**
   * Perfis retornados pela consulta do filtro atual — a filtragem é feita pela
   * API (`getPerfis`), não em memória.
   */
  readonly perfisFiltrados = this.perfisSignal.asReadonly();

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');

    if (id) {
      this.isConfiguracoesMode = false;
      this.carregarPerfil(Number(id));
    } else {
      this.isConfiguracoesMode = true;
      this.carregarPerfisPorFiltro();
    }
  }

  carregarPerfil(id: number): void {
    this.loaderService.showLoader();
    forkJoin({
      perfil: this.perfilService.getPerfilById(id),
      categorias: this.perfilService.getCategoriasByPerfil(id),
    })
      .pipe(
        take(1),
        finalize(() => this.loaderService.hideLoader()),
      )
      .subscribe({
        next: ({ perfil, categorias }) => {
          if (!perfil) {
            this.naoEncontrado.set(true);
            return;
          }
          this.perfilSignal.set({ ...perfil, categorias });
        },
        error: () => {
          this.naoEncontrado.set(true);
          this.toastService.show('Não foi possível carregar o perfil.', 'error');
        },
      });
  }

  /**
   * Consulta os perfis conforme o filtro selecionado e, na sequência, carrega as
   * categorias apenas dos perfis retornados nessa consulta.
   */
  carregarPerfisPorFiltro(): void {
    const filtro = this.filtroStatus();

    this.loaderService.showLoader();
    this.perfilService
      .getPerfis(this.filtrosDoStatus(filtro))
      .pipe(
        take(1),
        switchMap((perfis) =>
          perfis.length === 0
            ? of([] as Perfil[])
            : forkJoin(
                perfis.map((perfil) =>
                  this.perfilService
                    .getCategoriasByPerfil(perfil.id)
                    .pipe(map((categorias) => ({ ...perfil, categorias }))),
                ),
              ),
        ),
        finalize(() => this.loaderService.hideLoader()),
      )
      .subscribe({
        next: (perfis) => {
          // Descarta a resposta se o filtro já mudou, mantendo perfis e
          // categorias exibidos sempre consistentes com o filtro atual.
          if (this.filtroStatus() !== filtro) {
            return;
          }
          this.perfisSignal.set(perfis);
        },
        error: () => {
          if (this.filtroStatus() !== filtro) {
            return;
          }
          this.perfisSignal.set([]);
          this.toastService.show('Não foi possível carregar os perfis.', 'error');
        },
      });
  }

  setFiltroStatus(status: FiltroStatus): void {
    if (status === this.filtroStatus()) {
      return;
    }

    this.filtroStatus.set(status);
    this.carregarPerfisPorFiltro();
  }

  voltar(): void {
    void this.router.navigate(['/perfis']);
  }

  editar(): void {
    const perfil = this.perfil();
    if (perfil) {
      void this.router.navigate(['/perfil', perfil.id, 'editar']);
    }
  }

  editarPerfil(id: number): void {
    void this.router.navigate(['/perfil', id, 'editar']);
  }

  /**
   * `todos` consulta sem filtro; `Ativo`/`Inativo` vão como `status` para a API,
   * que já converte o valor para o código esperado pelo backend (`A`/`I`).
   */
  private filtrosDoStatus(filtro: FiltroStatus): PerfilFiltros | undefined {
    return filtro === 'todos' ? undefined : { status: filtro };
  }
}
